import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../styles/colors';
import FlagDivider from './FlagDivider';
import BrazilianColors from './BrazilianColors';
import cuoreImage from '../assets/images/cuore.png';

const AboutContainer = styled.section`
  padding: 6rem 0;
  background: linear-gradient(135deg, ${colors.neutral.lightGray} 0%, ${colors.neutral.white} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(0,150,57,0.05)"/><circle cx="75" cy="75" r="1" fill="rgba(0,150,57,0.05)"/><circle cx="50" cy="10" r="0.5" fill="rgba(0,150,57,0.05)"/><circle cx="10" cy="60" r="0.5" fill="rgba(0,150,57,0.05)"/><circle cx="90" cy="40" r="0.5" fill="rgba(0,150,57,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: start;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;


const MissionSection = styled.div`
  width: 100%;
  margin: 4rem 0;
  background: transparent;
  padding: 3rem 2rem;
  border-left: 4px solid ${colors.primary.green};
  text-align: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.primary.green}, ${colors.primary.yellow}, ${colors.primary.blue});
  }

  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: ${colors.text.primary};
    margin: 0;
    font-weight: 500;
    max-width: 1000px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const TextContent = styled.div`
  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin-bottom: 1rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      width: 60px;
      height: 4px;
      background: ${colors.primary.green};
      border-radius: 2px;
    }
  }

  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    color: ${colors.primary.green};
    margin-bottom: 1.5rem;
    font-style: italic;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: ${colors.text.secondary};
    margin-bottom: 1.5rem;
  }

`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 350px;
  margin: 0 auto;
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
  display: block;

  &:hover {
    transform: scale(1.05);
  }
`;

const DecorativeElement = styled.div`
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: ${colors.primary.green};
  border-radius: 50%;
  opacity: 0.1;
  z-index: -1;
`;

const CallToActionSection = styled.div`
  width: 100vw;
  margin: 4rem 0;
  background: linear-gradient(135deg, ${colors.primary.green}, ${colors.primary.blue});
  padding: 4rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.primary.yellow}, ${colors.primary.green}, ${colors.primary.blue});
  }

  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  p {
    font-size: 1.3rem;
    color: rgba(255,255,255,0.9);
    margin-bottom: 2.5rem;
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
    
    h3 {
      font-size: 2rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: white;
  color: ${colors.primary.green};
  text-decoration: none;
  border-radius: 4px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  border: 2px solid transparent;

  &:hover {
    background: ${colors.primary.yellow};
    color: ${colors.primary.green};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.3);
  }

  &.secondary {
    background: transparent;
    color: white;
    border: 2px solid white;

    &:hover {
      background: white;
      color: ${colors.primary.green};
    }
  }
`;


const About = () => {
  const { t } = useTranslation();

  const handleReservation = () => {
    const whatsappNumber = "393331054944";
    const message = "Ciao! Vorrei prenotare un tavolo al Cuore Brasiliano. Grazie!";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEvents = () => {
    const whatsappNumber = "393331054944";
    const message = "Ciao! Vorrei organizzare un evento al Cuore Brasiliano. Grazie!";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };


  return (
    <AboutContainer id="about">
      <Container>
        <Content>
          <TextContent>
            <motion.h2
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {t('about.title')}
            </motion.h2>
            
            <motion.h3
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              {t('about.subtitle')}
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {t('about.description')}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              {t('about.story')}
            </motion.p>

          </TextContent>

          <ImageContainer>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              style={{ width: '100%', height: '100%' }}
            >
              <Image src={cuoreImage} alt="Fachada do Restaurante Cuore Brasiliano" />
              <DecorativeElement />
            </motion.div>
          </ImageContainer>
        </Content>

        <MissionSection>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <p><strong>{t('about.mission')}</strong></p>
          </motion.div>
        </MissionSection>

        <CallToActionSection>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3>{t('about.cta.title')}</h3>
            <p>
              {t('about.cta.description')}
            </p>
            <ButtonContainer>
              <CTAButton
                as="button"
                onClick={handleReservation}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('about.cta.reserveTable')}
              </CTAButton>
              <CTAButton
                as="button"
                onClick={handleEvents}
                className="secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('about.cta.events')}
              </CTAButton>
            </ButtonContainer>
          </motion.div>
        </CallToActionSection>

        <FlagDivider />
        <BrazilianColors />
      </Container>
    </AboutContainer>
  );
};

export default About;
